import { AlertTriangle, ArrowRight, Clock3, Database, RadioTower, ShieldCheck, TimerReset } from 'lucide-react'
import type { AppData, PageKey } from '../types'
import { Card, HealthIcon, ScoreBar, Sparkline, StatusBadge } from '../components/UI'
import { PageHeader } from '../components/Layout'

const relative = (iso: string | null) => {
  if (!iso) return 'Not yet'
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minutes ago`
  const hours = Math.round(minutes / 60)
  return `${hours} hour${hours === 1 ? '' : 's'} ago`
}

export function HomePage({ data, setPage }: { data: AppData; setPage: (page: PageKey) => void }) {
  const urgent = data.trends.filter((trend) => trend.route === 'urgent')
  const warnings = data.health.filter((item) => item.status === 'warning' || item.status === 'failed')
  return (
    <>
      <PageHeader
        eyebrow="HDM Agency UAE"
        title="Good morning, Novin."
        body="Here is what changed, what needs your attention, and what the system is learning."
        actions={<><button className="button ghost" onClick={() => setPage('health')}><ShieldCheck /> System health</button><button className="button primary" onClick={() => setPage('trends')}><RadioTower /> View trends</button></>}
      />

      <div className="notice demo-notice"><ShieldCheck /><div><strong>Zero-cash guard is active</strong><span>Paid fallback is disabled. Private HDM strategy is blocked from free-tier AI.</span></div></div>

      <div className="metric-grid">
        <Card className="metric-card"><span>Urgent today</span><strong>{urgent.length}</strong><small>Maximum 2 by policy</small></Card>
        <Card className="metric-card"><span>Evidence remembered</span><strong>{data.evidenceCount}</strong><small>Permanent derived memory</small></Card>
        <Card className="metric-card"><span>Prediction accuracy</span><strong>{data.predictionAccuracy == null ? 'Warming up' : `${data.predictionAccuracy}%`}</strong><small>Needs evaluated predictions</small></Card>
        <Card className="metric-card"><span>Median lead time</span><strong>{data.medianLeadTime == null ? 'Warming up' : `${data.medianLeadTime}h`}</strong><small>Goal: at least 24 hours</small></Card>
      </div>

      <div className="content-grid two-thirds">
        <Card title="Signals requiring attention" action={<button className="text-button" onClick={() => setPage('trends')}>See all <ArrowRight /></button>}>
          <div className="signal-list">
            {data.trends.filter((trend) => trend.route !== 'archive').slice(0, 3).map((trend) => (
              <button className="signal-row" key={trend.id} onClick={() => setPage('trends')}>
                <div className="signal-main"><div><StatusBadge status={trend.route} /><StatusBadge status={trend.geography.toLowerCase()} /></div><strong>{trend.name}</strong><span>{trend.independentCreators} independent creators · {trend.platforms.join(' + ')}</span></div>
                <div className="signal-graph"><Sparkline values={trend.sparkline} /><small>{trend.lifecycle}</small></div>
                <div className="signal-score"><ScoreBar value={trend.emergence} /><small>emergence</small></div>
              </button>
            ))}
          </div>
        </Card>

        <div className="stack">
          <Card title="Run schedule">
            <div className="timeline-compact"><div><Clock3 /><span>Last scan</span><strong>{relative(data.lastScan)}</strong></div><div><TimerReset /><span>Next scan</span><strong>{new Date(data.nextScan).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong></div><div><Database /><span>Daily digest</span><strong>{data.settings.digestTime} Dubai</strong></div></div>
          </Card>
          <Card title="Needs setup">
            <div className="health-mini-list">
              {warnings.slice(0, 3).map((item) => <button key={item.name} onClick={() => setPage('setup')}><HealthIcon status={item.status} /><span><strong>{item.label}</strong><small>{item.message}</small></span><ArrowRight /></button>)}
              {!warnings.length && <div className="all-clear"><ShieldCheck /> Everything is connected.</div>}
            </div>
          </Card>
        </div>
      </div>

      <Card title="How the last decision was made" className="explain-card">
        <div className="explain-flow"><div><b>1</b><span><strong>Evidence</strong><small>Posts, subjects, audio and visuals</small></span></div><ArrowRight /><div><b>2</b><span><strong>Independence</strong><small>Reposts removed before counting</small></span></div><ArrowRight /><div><b>3</b><span><strong>Change</strong><small>Compared with remembered history</small></span></div><ArrowRight /><div><b>4</b><span><strong>HDM fit</strong><small>DNA + active weekly context</small></span></div></div>
        <div className="explain-warning"><AlertTriangle /> Every alert includes evidence and a “what could make this wrong” section.</div>
      </Card>
    </>
  )
}

